import useProductContext from '@/context/ProductContext';
import { ShopifyVariant } from '@/lib/shopify';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

const VARIANT_BASE_URL = 'gid://shopify/ProductVariant/';

export const useVariantSelection = (cb?: (variant: ShopifyVariant) => void) => {
  const { setCarouselImagePosition } = useProductContext();
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getVariantImageIndex = useCallback((id: string) => {
    const carouselContainer = document.getElementsByClassName(
      'luv-carousel product-slider',
    );
    if (carouselContainer.length) {
      const slide = carouselContainer[0].querySelectorAll(
        `[data-imageid='${id}']`,
      );

      if (slide.length) {
        const index = (slide[0].parentNode?.parentNode as Element).getAttribute(
          'data-index',
        );
        if (index !== null) return index;
      }
    }

    return null;
  }, []);

  const handleVariantClick = useCallback(
    (variant: ShopifyVariant) => {
      const params = new URLSearchParams(searchParams || {});
      const imagePosition = getVariantImageIndex(variant.id);
      if (imagePosition !== null) setCarouselImagePosition(imagePosition);

      const variantId = variant.id.split(VARIANT_BASE_URL)[1];
      params.set('variant', variantId);
      push(`${pathname}?${params.toString()}`);

      cb?.(variant);
    },
    [
      cb,
      getVariantImageIndex,
      pathname,
      push,
      setCarouselImagePosition,
      searchParams,
    ],
  );

  useEffect(() => {
    const imagePosition = getVariantImageIndex(
      `${VARIANT_BASE_URL}${searchParams?.get('variant')}`,
    );
    if (imagePosition !== null) setCarouselImagePosition(imagePosition);
  }, [searchParams, setCarouselImagePosition, getVariantImageIndex]);

  return {
    handleVariantClick,
    getVariantImageIndex,
  };
};
